import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedDate, FormattedNumber } from 'react-intl';

import { Button, ButtonSize, Rounding } from '../../../shared/components/button/button';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Gap } from '../../../shared/components/gap/gap';
import { Icon } from '../../../shared/components/icon/icon';
import { IconHover } from '../../../shared/components/icon-hover/icon-hover';
import { Loader } from '../../../shared/components/loader/loader';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { useContextNavigate } from '../../../shared/hooks/use-context-navigate';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { ensureIsoDateWithTimeZone } from '../../../shared/utils/date';
import { slugify } from '../../../shared/utils/strings';
import type { IUserTransaction } from '../../queries/transactions.graphql';
import { TransactionsOperationType, TransactionsStatus } from '../../transactions.interface';

import styles from './wallet-transactions.module.scss';

const style = createStyleHelper(styles, 'wallet-transactions');

export interface ITransactionsProps {
    className?: string;
    activityData: IUserTransaction[];
    onLoadMore: () => void;
    hasMore: boolean;
    fetching: boolean;
}

export const WalletTransactionsComponent: FunctionComponent<ITransactionsProps> = ({
    className,
    activityData,
    onLoadMore,
    hasMore,
    fetching,
}) => {
    const isDesktop = useDesktop();
    const navigate = useContextNavigate();

    const repeatDeposit = useCallback(
        (id: string) => {
            navigate('/cashier/deposit');
            console.warn('repeatDeposit', id);
        },
        [navigate],
    );

    const cancelWithdrawal = useCallback(
        (id: string) => {
            navigate('/cashier/withdrawal');
            console.warn('cancelWithdrawal', id);
        },
        [navigate],
    );

    return (
        <div className={createClassName([className, style()])}>
            <div className={style('table', ['heading'])}>
                <div className={style('date-and-time')}>
                    <FormattedMessage
                        id="account.transactions.date-and-time"
                        defaultMessage="Date and Time"
                    />
                </div>
                <div className={style('method')}>
                    <FormattedMessage id="account.transactions.method" defaultMessage="Method" />
                </div>
                <div className={style('status')}>
                    <FormattedMessage id="account.transactions.status" defaultMessage="Status" />
                </div>
                <div className={style('previous-balance')}>
                    <FormattedMessage id="account.transactions.before" defaultMessage="Before" />
                </div>
                <div className={style('amount')}>
                    <FormattedMessage id="account.transactions.amount" defaultMessage="Amount" />
                </div>
                <div className={style('current-balance')}>
                    <FormattedMessage id="account.transactions.after" defaultMessage="After" />
                </div>
                <div className={style('action')}></div>
            </div>
            {activityData.length
                ? activityData.map(
                      ({
                          additionalProperties,
                          operationType,
                          transactionStatus,
                          lastUpdatedOn,
                          transactionId,
                          previousBalanceAmount,
                          transactionAmount,
                          balanceAmount,
                          currencyCode,
                          ipAddress,
                          geoCountry,
                          geoCity,
                          geoRegion,
                      }) => (
                          <div className={style('table')} key={`${transactionId}.${lastUpdatedOn}`}>
                              <div className={style('date-and-time')}>
                                  <IconHover
                                      hoverItem={
                                          <Gap direction="horizontal">
                                              <FormattedDate
                                                  value={
                                                      lastUpdatedOn
                                                          ? ensureIsoDateWithTimeZone(lastUpdatedOn)
                                                          : ''
                                                  }
                                                  dateStyle={'short'}
                                                  timeStyle={'short'}
                                              />
                                              <Icon name={'info-circle'} />
                                          </Gap>
                                      }
                                      infoDisplay={
                                          <FormattedMessage
                                              id="account.transactions.wallet.info"
                                              defaultMessage="IP Address: {ipAddress}<br></br>Geo Country: {geoCountry}<br></br>Geo City: {geoCity}<br></br>Geo Region: {geoRegion}"
                                              values={{
                                                  ipAddress,
                                                  geoCountry,
                                                  geoCity,
                                                  geoRegion,
                                              }}
                                          />
                                      }
                                  />
                              </div>
                              <div className={style('method')}>
                                  {additionalProperties?.Payments?.Transaction?.PaymentMethod ===
                                  'Manual Adjustment' ? (
                                      <FormattedMessage
                                          id="account.transactions.manual-adjustment"
                                          defaultMessage="{paymentMethod} Description: {reason}"
                                          values={{
                                              paymentMethod:
                                                  additionalProperties?.Payments?.Transaction
                                                      ?.PaymentMethod ?? 'N/A',
                                              reason:
                                                  additionalProperties?.Payments?.Transaction
                                                      ?.Reason ?? 'N/A',
                                          }}
                                      />
                                  ) : (
                                      <FormattedMessage
                                          id="account.transactions.method-name"
                                          defaultMessage="{paymentMethod}"
                                          values={{
                                              paymentMethod:
                                                  additionalProperties?.Payments?.Transaction
                                                      ?.PaymentMethod ?? 'N/A',
                                          }}
                                      />
                                  )}
                              </div>
                              <div className={style('status')}>
                                  <FormattedMessage
                                      id={`account.transactions.status.${slugify(
                                          transactionStatus ?? '',
                                      )}`}
                                      defaultMessage="N/A"
                                  />
                              </div>
                              <div className={style('previous-balance')}>
                                  {previousBalanceAmount ? (
                                      <FormattedNumber
                                          value={previousBalanceAmount}
                                          currency={currencyCode ?? ''}
                                          style={'currency'}
                                      />
                                  ) : (
                                      <FormattedMessage
                                          id="account.transactions.no-data"
                                          defaultMessage="N/A"
                                      />
                                  )}
                              </div>
                              <div className={style('amount')}>
                                  <FormattedNumber
                                      value={transactionAmount}
                                      currency={currencyCode ?? ''}
                                      style={'currency'}
                                  />
                                  {(transactionStatus === TransactionsStatus.COMPLETED ||
                                      transactionStatus ===
                                          TransactionsStatus.PENDINGAUTHORIZATION) &&
                                      !isDesktop && (
                                          <div
                                              className={style('status-color', [
                                                  transactionStatus.toLowerCase(),
                                              ])}
                                          >
                                              <FormattedMessage
                                                  id={`account.transactions.status.${slugify(
                                                      transactionStatus,
                                                  )}`}
                                                  defaultMessage="N/A"
                                              />
                                          </div>
                                      )}
                              </div>
                              <div className={style('current-balance')}>
                                  {balanceAmount ? (
                                      <FormattedNumber
                                          value={balanceAmount}
                                          currency={currencyCode ?? ''}
                                          style={'currency'}
                                      />
                                  ) : (
                                      <FormattedMessage
                                          id="account.transactions.no-data"
                                          defaultMessage="N/A"
                                      />
                                  )}
                              </div>
                              <div className={style('action')}>
                                  {operationType === TransactionsOperationType.CREDIT && (
                                      <Button
                                          className={style('button-repeat')}
                                          size={ButtonSize.MEDIUM}
                                          hollow
                                          disabled
                                          rounding={Rounding.NONE}
                                          onClick={() => repeatDeposit(transactionId ?? '')}
                                      >
                                          <FormattedMessage
                                              id="account.transactions.repeat-deposit"
                                              defaultMessage="Repeat Deposit"
                                          />
                                      </Button>
                                  )}
                                  {operationType === TransactionsOperationType.DEBIT && (
                                      <Button
                                          className={style('button-cancel')}
                                          size={ButtonSize.MEDIUM}
                                          warning
                                          disabled
                                          rounding={Rounding.NONE}
                                          onClick={() => cancelWithdrawal(transactionId ?? '')}
                                      >
                                          <FormattedMessage
                                              id="account.transactions.cancel-withdrawal"
                                              defaultMessage="Cancel Withdrawal"
                                          />
                                      </Button>
                                  )}
                              </div>
                          </div>
                      ),
                  )
                : null}

            {fetching && activityData.length === 0 ? (
                <div className={style('message')}>
                    <Loader />
                </div>
            ) : null}

            {!activityData.length ? (
                <div className={style('message')}>
                    <FormattedMessage
                        id="account.transactions.no-data-to-display"
                        defaultMessage="No data to display"
                    />
                </div>
            ) : null}

            {hasMore && activityData.length > 0 && !fetching && (
                <div className={style('button-section')}>
                    <Button
                        className={style('load-more')}
                        size={ButtonSize.MEDIUM}
                        rounding={Rounding.SMALL}
                        onClick={onLoadMore}
                    >
                        <FormattedMessage
                            id="account.transactions.load-more"
                            defaultMessage="Load More"
                        />
                    </Button>
                </div>
            )}
        </div>
    );
};
